import React, { useEffect, useMemo, useRef, useState } from "react"
import { Modal, Input, InputRef } from "antd"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"

import { SearchResult } from "features/Docs/types/docs.types"

interface DocsSearchModalProps {
  open: boolean
  onClose: () => void
  search: (query: string) => SearchResult[]
}

const MAX_RESULTS = 24

const escapeRegExp = (term: string): string =>
  term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/** Wrap matched query terms in <mark> for display. */
const highlightTerms = (text: string, terms: string[]): React.ReactNode => {
  const escaped = terms.map(escapeRegExp).filter(Boolean)
  if (!text || escaped.length === 0) return text
  const lookup = new Set(terms.map((term) => term.toLowerCase()))
  const regex = new RegExp(`(${escaped.join("|")})`, "gi")
  return text.split(regex).map((part, index) =>
    part && lookup.has(part.toLowerCase()) ? (
      <mark key={index} className="docs-search__mark">
        {part}
      </mark>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    ),
  )
}

/** A short body excerpt for a result when it has no description. */
const excerpt = (result: SearchResult): string => {
  if (result.record.description) return result.record.description
  const body = result.record.plainBody
  return body.length > 150 ? `${body.slice(0, 150)}…` : body
}

/**
 * Command-palette style search over the docs. Grouped by section, with
 * <mark> highlighting, arrow-key navigation, and Enter/click to open — deep
 * linking to a matched heading when there is one.
 */
const DocsSearchModal: React.FC<DocsSearchModalProps> = ({
  open,
  onClose,
  search,
}) => {
  const navigate = useNavigate()
  const inputRef = useRef<InputRef>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)

  const terms = useMemo(
    () => query.toLowerCase().split(/\s+/).filter(Boolean),
    [query],
  )

  const results = useMemo(
    () => (query.trim() ? search(query).slice(0, MAX_RESULTS) : []),
    [query, search],
  )

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  useEffect(() => {
    if (open) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 60)
      return () => window.clearTimeout(timer)
    }
    setQuery("")
  }, [open])

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(
      `[data-result-index="${activeIndex}"]`,
    )
    node?.scrollIntoView({ block: "nearest" })
  }, [activeIndex, results])

  const goToResult = (result: SearchResult): void => {
    navigate(
      result.headingSlug
        ? `${result.record.route}#${result.headingSlug}`
        : result.record.route,
    )
    onClose()
  }

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, results.length - 1))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === "Enter") {
      event.preventDefault()
      const result = results[activeIndex]
      if (result) goToResult(result)
    }
  }

  // Group results by section while preserving score order and global index.
  const groups: Array<{ title: string; items: Array<{ result: SearchResult; index: number }> }> = []
  results.forEach((result, index) => {
    const last = groups[groups.length - 1]
    if (last && last.title === result.record.sectionTitle) {
      last.items.push({ result, index })
    } else {
      groups.push({
        title: result.record.sectionTitle,
        items: [{ result, index }],
      })
    }
  })

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      className="docs-search-modal"
      title={null}
      closable={false}
    >
      <div className="docs-search">
        <div className="docs-search__input-wrap">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="docs-search__input-icon"
          />
          <Input
            ref={inputRef}
            variant="borderless"
            size="large"
            placeholder="Search the documentation…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Search documentation"
          />
        </div>

        <div className="docs-search__results" ref={listRef}>
          {query.trim() && results.length === 0 && (
            <p className="docs-search__empty">
              No matches for &ldquo;{query}&rdquo;. Try a keyword like{" "}
              <code>XTAB</code>, <code>RECODE</code>, or <code>routing</code>.
            </p>
          )}

          {groups.map((group) => (
            <div className="docs-search__group" key={group.title}>
              <p className="docs-search__group-title">{group.title}</p>
              {group.items.map(({ result, index }) => (
                <button
                  key={result.record.route}
                  type="button"
                  data-result-index={index}
                  className={`docs-search__result${
                    index === activeIndex ? " docs-search__result--active" : ""
                  }`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => goToResult(result)}
                >
                  <span className="docs-search__result-title">
                    {highlightTerms(result.record.pageTitle, terms)}
                  </span>
                  <span className="docs-search__result-excerpt">
                    {highlightTerms(excerpt(result), terms)}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="docs-search__footer">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> to navigate
          </span>
          <span>
            <kbd>↵</kbd> to open
          </span>
          <span>
            <kbd>Esc</kbd> to close
          </span>
        </div>
      </div>
    </Modal>
  )
}

export default DocsSearchModal
